import { useCallback, useMemo } from "react";
import {
  FreeTextSection,
  OptionGroupsSection,
  CHIEF_COMPLAINT_PLACEHOLDER,
  ReadmissionSectionRenderer,
  SectionDef,
  UpdateKeyedField,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import CodeSection from "./CodeSection";
import RosPeSection from "./RosPeSection";
import HomeHealthAssessmentSection, {
  HOME_HEALTH_ASSESSMENTS,
} from "./HomeHealthAssessmentSection";
import ScreeningSection from "./ScreeningSection";
import MiniCogSection from "./MiniCogSection";
import {
  INITIAL_F2F_OPTION_SECTIONS,
  LIBRARY_TEMPLATE_SECTIONS,
} from "../initial-f2f-constant";
import {
  EncounterKind,
  createFaceToFaceGroups,
} from "../initial-f2f-plan-text-constant";
import { CODE_BLOCKS } from "../initial-f2f-code-constant";
import { DateLabelOverrides, InitialF2fPayload } from "../initial-f2f-types";
import { applyDateLabels, buildDateSummaryEntries } from "../initial-f2f-utils";

export interface InitialF2fSectionRendererProps {
  section: SectionDef;
  expanded: boolean;
  onToggle: () => void;
  payload: InitialF2fPayload;
  update: (patch: Partial<InitialF2fPayload>) => void;
  updateKeyedField: UpdateKeyedField<InitialF2fPayload>;
  chiefComplaint: string;
  onChiefComplaintChange: (value: string) => void;
  encounterKind?: EncounterKind;
  dateLabels?: DateLabelOverrides;
  appointmentId?: string;
  clockDrawingUrl?: string;
}

const InitialF2fSectionRenderer = ({
  section,
  expanded,
  onToggle,
  payload,
  update,
  updateKeyedField,
  chiefComplaint,
  onChiefComplaintChange,
  encounterKind,
  dateLabels,
  appointmentId,
  clockDrawingUrl,
}: InitialF2fSectionRendererProps) => {
  const { key, title } = section;
  const updateSectionFreeText = useCallback(
    (freeTextKey: string, value: string) =>
      updateKeyedField("sectionFreeText", freeTextKey, value),
    [updateKeyedField]
  );
  const optionSection = INITIAL_F2F_OPTION_SECTIONS[key];
  const dateGroup =
    optionSection?.dateGroup &&
    applyDateLabels(optionSection.dateGroup, dateLabels);
  // Stable group definitions keep the memoised option items from re-rendering.
  const optionGroups = useMemo(
    () =>
      key === "faceToFace" && encounterKind
        ? createFaceToFaceGroups(encounterKind)
        : optionSection?.groups,
    [key, encounterKind, optionSection]
  );
  const base = { title, expanded, onToggle };

  if (key in HOME_HEALTH_ASSESSMENTS) {
    return <HomeHealthAssessmentSection {...base} assessmentKey={key} />;
  }
  const dateValues = payload.sectionDates[key] ?? {};
  const optionStore = payload.sectionOptions[key] ?? {};
  const extraSummaryLines = dateGroup
    ? buildDateSummaryEntries(dateGroup, dateValues)
    : [];

  switch (key) {
    case "chiefComplaints":
      return (
        <FreeTextSection
          {...base}
          value={chiefComplaint}
          onChange={onChiefComplaintChange}
          placeholder={CHIEF_COMPLAINT_PLACEHOLDER}
        />
      );
    case "reviewOfSystems":
      return (
        <RosPeSection
          {...base}
          template={LIBRARY_TEMPLATE_SECTIONS.reviewOfSystems}
        />
      );
    case "physicalExam":
      return (
        <RosPeSection
          {...base}
          template={LIBRARY_TEMPLATE_SECTIONS.physicalExam}
        />
      );
    case "screening":
      return (
        <ScreeningSection
          {...base}
          customAssessments={payload.screeningCustom}
          onCustomAssessmentsChange={(screeningCustom) =>
            update({ screeningCustom })
          }
          freeText={payload.screeningFreeText}
          onFreeTextChange={(screeningFreeText) =>
            update({ screeningFreeText })
          }
        />
      );
    case "miniCog":
      return (
        <MiniCogSection
          {...base}
          value={payload.miniCog}
          onChange={(miniCog) => update({ miniCog })}
          appointmentId={appointmentId}
          clockDrawingUrl={clockDrawingUrl}
        />
      );
    case "diagnosis":
      return (
        <CodeSection
          {...base}
          block={CODE_BLOCKS.diagnosis}
          codes={payload.diagnosisCodes}
          onCodesChange={(diagnosisCodes) => update({ diagnosisCodes })}
          freeText={payload.diagnosisFreeText}
          onFreeTextChange={(diagnosisFreeText) =>
            update({ diagnosisFreeText })
          }
        />
      );
    case "procedureCodes":
      return (
        <CodeSection
          {...base}
          block={CODE_BLOCKS.cpt}
          codes={payload.cptCodes}
          onCodesChange={(cptCodes) => update({ cptCodes })}
          freeText={payload.cptFreeText}
          onFreeTextChange={(cptFreeText) => update({ cptFreeText })}
        />
      );
    default:
      if (optionSection) {
        return (
          <OptionGroupsSection
            {...base}
            groups={optionGroups ?? optionSection.groups}
            hint={optionSection.hint}
            extraSummaryLines={extraSummaryLines}
            store={optionStore}
            onChange={(store) => updateKeyedField("sectionOptions", key, store)}
          />
        );
      }
      return (
        <ReadmissionSectionRenderer
          section={section}
          expanded={expanded}
          onToggle={onToggle}
          payload={payload}
          update={update}
          updateSectionFreeText={updateSectionFreeText}
        />
      );
  }
};

export default InitialF2fSectionRenderer;
